import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PathService } from './path.service';

/**
 * One option's public aggregates as written by the whocan-votesCounter trigger
 * (whoo/voteCounts/{ownerId}/{pollId}/{optionIndex}, see functions-lib/paths.ts).
 */
export interface OptionVoteCounts {
    positiveCount?: number;
    personsSum?: number;
}

/**
 * Event-wide sums of the "Will you join?" block under the reserved key `_header`
 * (workorder-header-max-persons-2026-09-10, task 3): acceptedCount = votes with
 * choiceInvitation 1, personsTotal = their adults + kids (at least 1 per acceptance).
 */
export interface HeaderVoteCounts {
    acceptedCount?: number;
    personsTotal?: number;
}

/** reserved key of the header sums inside the voteCounts node - never an option index */
const HEADER_COUNTS_KEY = '_header';

@Injectable({
    providedIn: 'root'
})
/**
 * Read side of the public per-option vote aggregates
 * (workorder-option-counts-2026-08-31). Subscribed by PollService ONLY when the
 * current user may not read the votes list (participant on a poll with
 * showParticipants=false) - everybody else keeps aggregating the loaded vote list,
 * that path stays untouched.
 *
 * A realtime subscription, not a one-time read: "option is full" must
 * disable/enable live while the voter is on the page. A missing node (poll
 * without votes, backfill gap) simply means 0 everywhere.
 */
export class VoteCountsService {
    private _countsByOptionIndex = new Map<number, OptionVoteCounts>();
    private _headerCounts: HeaderVoteCounts | null = null;
    private _subscription: Subscription | null = null;
    private _isActive = false;

    constructor(
        private logger: LoggerService,
        private afd: AngularFireDatabase,
        private pathService: PathService,
    ) {
    }

    /** true while the count fallback feeds the limit checks (votes list not readable) */
    get isActive(): boolean {
        return this._isActive;
    }

    /** YES + MAYBE responses for the option; 0 when the node is missing */
    public getPositiveCount(optionIndex: number): number {
        return this._countsByOptionIndex.get(optionIndex)?.positiveCount || 0;
    }

    /** registered persons (sum of choice.adultsCount where YES); 0 when the node is missing */
    public getPersonsSum(optionIndex: number): number {
        return this._countsByOptionIndex.get(optionIndex)?.personsSum || 0;
    }

    /** votes with choiceInvitation 1 (whole event); 0 when the node is missing */
    public getAcceptedCount(): number {
        return this._headerCounts?.acceptedCount || 0;
    }

    /** registered persons incl. kids over all acceptances (header people limit); 0 when missing */
    public getPersonsTotal(): number {
        return this._headerCounts?.personsTotal || 0;
    }

    public subscribe(): void {
        this.uninitialize();
        this._isActive = true;
        this._subscription = this.afd.object<{ [key: string]: OptionVoteCounts | HeaderVoteCounts }>(
            this.pathService.getVoteCountsPath()
        ).valueChanges().pipe(
            catchError(error => {
                // never break the vote page over the aggregates - degrade to "no counts"
                this.logger.warn('voteCounts read failed, falling back to empty counts', error);
                return of(null);
            })
        ).subscribe(node => {
            this.applyNode(node);
        });
    }

    public uninitialize(): void {
        this._subscription?.unsubscribe();
        this._subscription = null;
        this._countsByOptionIndex.clear();
        this._headerCounts = null;
        this._isActive = false;
    }

    /**
     * firebase returns dense numeric keys as a real array, sparse ones as a map - handle both.
     * The `_header` entry is kept apart from the option map (Number('_header') is NaN).
     */
    private applyNode(node: { [key: string]: OptionVoteCounts | HeaderVoteCounts } | OptionVoteCounts[] | null): void {
        this._countsByOptionIndex.clear();
        this._headerCounts = null;
        if (node && typeof node === 'object') {
            Object.entries(node).forEach(([key, counts]) => {
                if (!counts) return;
                if (key === HEADER_COUNTS_KEY) {
                    this._headerCounts = counts as HeaderVoteCounts;
                } else {
                    this._countsByOptionIndex.set(Number(key), counts as OptionVoteCounts);
                }
            });
        }
        this.logger.log('voteCounts updated, options with counts:', this._countsByOptionIndex.size,
            'persons total:', this.getPersonsTotal());
    }
}
